import styled, { css } from 'styled-components';
import * as animations from '../../styles/animations';
import { Container } from '../../styles/sharedComponents';

export const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 184px 0 80px; /* 64px (nav) + 120px (espaço original) */
  background:
    radial-gradient(600px circle at 50% 200px, rgba(255, 68, 68, 0.05), transparent),
    radial-gradient(400px circle at 20% 300px, rgba(120, 119, 198, 0.03), transparent),
    radial-gradient(400px circle at 80% 300px, rgba(255, 68, 68, 0.03), transparent);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 80%, rgba(255, 68, 68, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(120, 119, 198, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(255, 68, 68, 0.05) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, transparent 0%, rgba(10, 10, 10, 0.2) 20%, rgba(10, 10, 10, 0.5) 40%, rgba(10, 10, 10, 0.8) 70%, rgba(10, 10, 10, 1) 100%);
    pointer-events: none;
    z-index: 1;
  }

  @media (max-width: 768px) {
    min-height: 80vh;
    padding: 156px 0 60px; /* 56px (nav) + 100px (espaço original) */
  }
`;

export const FloatingParticle = styled.div<{ $delay: number; $x: number; $duration: number }>`
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(255, 68, 68, 0.3);
  border-radius: 50%;
  left: ${props => props.$x}%;
  bottom: 0;
  ${props => css`
    animation: ${animations.particleFloat} ${props.$duration}s linear infinite;
    animation-delay: ${props.$delay}s;
  `}
  z-index: 1;
`;

export const ParrotLogo = styled.img.attrs({
  loading: 'lazy',
  decoding: 'async',
  width: '320',
  height: 'auto'
})`
  position: absolute;
  right: 80px;
  top: 35%;
  transform: translateY(-50%);
  width: 320px;
  height: auto;
  opacity: 0.15;
  z-index: 0;
  pointer-events: none;
  filter: brightness(1.4) contrast(1.3);
  animation: ${animations.floatingAnimation} 8s ease-in-out infinite;

  @media (max-width: 1200px) {
    right: 20px;
    top: 40%;
    width: 280px;
    opacity: 0.12;
  }

  @media (max-width: 768px) {
    right: -50px;
    top: 45%;
    width: 220px;
    opacity: 0.08;
  }

  @media (max-width: 480px) {
    right: -80px;
    top: 50%;
    width: 160px;
    opacity: 0.05;
  }
`;

export const HeroContent = styled(Container)`
  text-align: center;
  position: relative;
  z-index: 1;
`;

export const HeroBadgeStyled = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${props => props.theme.borders.radius.full};
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #a1a1aa;
  margin-bottom: 24px;
  animation: ${animations.slideInUp} 0.7s cubic-bezier(0.25, 0.1, 0.25, 1) 0.1s both;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 4px 12px;
  }
`;

export const HeroTitle = styled.h1`
  font-size: clamp(48px, 8vw, 80px);
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px;
  background: linear-gradient(180deg, #fafafa 0%, #a1a1aa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${animations.slideInUp} 0.7s cubic-bezier(0.25, 0.1, 0.25, 1) 0.2s both;

  @media (max-width: 768px) {
    margin-bottom: 16px;
    line-height: 1.2;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(18px, 2.5vw, 24px);
  color: #71717a;
  margin: 0 auto 40px;
  max-width: 600px;
  line-height: 1.5;
  animation: ${animations.slideInUp} 0.7s cubic-bezier(0.25, 0.1, 0.25, 1) 0.3s both;

  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
`;

export const HeroActionsStyled = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 80px;
  animation: ${animations.slideInUp} 0.7s cubic-bezier(0.25, 0.1, 0.25, 1) 0.4s both;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-bottom: 60px;
  }
`;

export const HeroCtaButton = styled.button`
  background: linear-gradient(135deg, #ff4444, #ff2222);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 16px 32px;
  font-size: 18px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  box-shadow: 0 4px 20px rgba(255, 68, 68, 0.3);
  position: relative;
  overflow: hidden;
  will-change: transform, box-shadow;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.6s cubic-bezier(0.25, 0.1, 0.25, 1);
  }

  &::after {
    content: '';
    position: absolute;
    z-index: -1;
    top: 50%;
    left: 50%;
    width: 300px;
    height: 300px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 12px 40px rgba(255, 68, 68, 0.5);
    background: linear-gradient(135deg, #ff5555, #ff3333);

    &::before {
      left: 100%;
    }

    &::after {
      transform: translate(-50%, -50%) scale(1);
    }
  }

  &:active {
    transform: translateY(-1px) scale(0.98);
    transition: all 0.1s ease;
  }

  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 16px;
  }
`;
